import type { components } from "@/api/types.gen";
import type { Material } from "@/api/schemas";
import { formatEur, toNum } from "@/lib/format";

type Dvojicka = components["schemas"]["DvojickaItem"];

function cenaSMj(d: Dvojicka, mj: string): string {
  return d.mj.toLowerCase() === mj.toLowerCase()
    ? formatEur(d.nakup_cena)
    : `${formatEur(d.nakup_cena)}/${d.mj}`;
}

/**
 * Štítok „aj u: Nábytkár 2,03 €" pri názve v Cenníku (KOLO 166).
 *
 * Ten istý výrobok (kód výrobcu) u iných dodávateľov — jeden zástupca na
 * dodávateľa vyberá server (`services/dvojicky`: rovnaká MJ, rovnaká
 * farba, najlacnejší). Šípka ↓ = u iného je lacnejšie v tej istej MJ.
 * Pri riadku, ktorý výbery neponúkajú (`nepreferovany`), dopíše, koho
 * ponúkajú namiesto neho — inak by user nevedel, prečo ho picker nenájde.
 */
export function DvojickyZnak({
  material,
}: {
  material: Pick<Material, "dvojicky" | "nakup_cena" | "mj" | "nepreferovany">;
}): JSX.Element | null {
  const dvojicky = material.dvojicky ?? [];
  const prva = dvojicky[0];
  if (prva === undefined) return null;

  const mojaCena = toNum(material.nakup_cena);
  const lacnejsie = dvojicky.some(
    (d) =>
      d.mj.toLowerCase() === material.mj.toLowerCase() &&
      toNum(d.nakup_cena) < mojaCena,
  );
  const text =
    dvojicky.length === 1
      ? `aj u: ${prva.dodavatel_nazov} ${cenaSMj(prva, material.mj)}`
      : `aj u: ${dvojicky.map((d) => d.dodavatel_nazov).join(", ")} · od ${formatEur(
          Math.min(...dvojicky.map((d) => toNum(d.nakup_cena))),
        )}`;
  const preferovany = dvojicky.find((d) => d.preferovany);
  const title = [
    ...dvojicky.map(
      (d) =>
        `${d.preferovany ? "★ " : ""}${d.dodavatel_nazov}: ${d.nazov} — ${cenaSMj(
          d,
          material.mj,
        )}` + (d.pocet_variant > 1 ? ` (+${d.pocet_variant - 1} variantov)` : ""),
    ),
    ...(material.nepreferovany && preferovany
      ? [`Výbery ponúkajú preferovaného: ${preferovany.dodavatel_nazov}`]
      : []),
  ].join("\n");

  return (
    <span className="ml-1.5 inline-flex flex-wrap items-center gap-1 align-middle">
      <span
        className="rounded-full border border-input bg-background-alt px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
        title={title}
      >
        {lacnejsie ? "↓ " : ""}
        {text}
      </span>
      {material.nepreferovany && preferovany ? (
        <span
          className="text-[10px] text-muted-foreground"
          title="Preferovaný dodávateľ značky — Nastavenia → Ceny a cenníky → Preferovaní dodávatelia"
        >
          vo výberoch {preferovany.dodavatel_nazov}
        </span>
      ) : null}
    </span>
  );
}
